import { useEffect, useId, useRef, useState } from 'react'
import { Headphones, Users } from 'lucide-react'
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon'
import { FLOAT_ACTION_SIZE, FLOAT_WHATSAPP_BOTTOM } from '@/components/layout/floatingActions'
import { useSite } from '@/context/SiteSettingsContext'
import { whatsappHref } from '@/lib/mergeSiteSettings'
import { useCityStore, DEFAULT_CITY_NAME } from '@/store/cityStore'
import { cn } from '@/lib/utils'
import { containsTarget } from '@/lib/dom'

export function WhatsAppFloat() {
  const site = useSite()
  const cityName = useCityStore((s) => s.cityName) || DEFAULT_CITY_NAME
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const closeTimer = useRef<number | null>(null)

  const expertHref = whatsappHref(site.whatsapp, 'Hello QBC Support — I would like to talk to an expert.')
  const communityHref = (site.whatsappLink || '').trim()

  const clearCloseTimer = () => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const openMenu = () => {
    clearCloseTimer()
    setOpen(true)
  }

  const scheduleClose = () => {
    clearCloseTimer()
    closeTimer.current = window.setTimeout(() => setOpen(false), 160)
  }

  useEffect(() => {
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containsTarget(rootRef.current, event.target)) {
        setOpen(false)
      }
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
      document.removeEventListener('keydown', onKey)
      clearCloseTimer()
    }
  }, [])

  return (
    <div
      ref={rootRef}
      data-track-location="whatsapp_float"
      // The closed menu still takes up space; only the icon and the open menu may react to hover.
      className={cn('pointer-events-none fixed right-4 z-40 flex flex-col items-end gap-2', FLOAT_WHATSAPP_BOTTOM)}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <div
        id={menuId}
        role="menu"
        aria-hidden={!open}
        className={cn(
          'origin-bottom-right overflow-hidden rounded-2xl border border-white/70 bg-white/95 shadow-[0_12px_40px_rgba(6,59,102,0.18)] backdrop-blur-md transition-all duration-200 ease-out',
          open
            ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
            : 'pointer-events-none translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="min-w-[220px] p-1.5">
          <p className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-qbc-navy/55">
            WhatsApp
          </p>
          <a
            role="menuitem"
            href={expertHref}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[#25D366]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]/40"
            onClick={() => setOpen(false)}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#25D366]/15 text-[#128C7E] transition-transform group-hover:scale-105">
              <Headphones className="h-4 w-4" strokeWidth={2.25} />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-qbc-navy">Talk to expert</span>
              <span className="block truncate text-[11px] text-slate-500">Chat with QBC support</span>
            </span>
          </a>

          {communityHref ? (
            <a
              role="menuitem"
              href={communityHref}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-[#25D366]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]/40"
              onClick={() => setOpen(false)}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-qbc-navy/10 text-qbc-navy transition-transform group-hover:scale-105">
                <Users className="h-4 w-4" strokeWidth={2.25} />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-qbc-navy">Join community</span>
                <span className="block truncate text-[11px] text-slate-500">{cityName} WhatsApp group</span>
              </span>
            </a>
          ) : null}
        </div>
      </div>

      <button
        type="button"
        aria-label="WhatsApp options"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'pointer-events-auto flex items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#1ebe5d] hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366]/50',
          FLOAT_ACTION_SIZE,
          open && 'ring-4 ring-[#25D366]/25',
        )}
      >
        <WhatsAppIcon className="h-7 w-7" />
      </button>
    </div>
  )
}
